/**
 * Public integrations catalog (marketing page /integrations).
 *
 * Mirrors backend/core/config.py::INTEGRATION_TYPES (48 connectors). Kept static
 * on purpose so the page renders without an API call and can be pre-rendered for
 * search engines. When a connector is added to INTEGRATION_TYPES, add it here and
 * give it a logo in components/ProviderLogo.jsx.
 */

export const INTEGRATION_CATEGORIES = [
  {
    id: "boards",
    title: { pt: "Boards e tickets", en: "Boards & ticketing", es: "Tableros y tickets" },
    desc: {
      pt: "De onde o card chega. O Ratchet lê o ticket, extrai identificadores e devolve o diagnóstico pronto para o card.",
      en: "Where the card comes from. Ratchet reads the ticket, extracts identifiers and returns a diagnosis ready for the card.",
      es: "De donde llega el ticket. Ratchet lo lee, extrae identificadores y devuelve el diagnóstico listo para el ticket.",
    },
    items: [
      { key: "jira", label: "Atlassian Jira" },
      { key: "azure_devops", label: "Azure DevOps / TFS" },
      { key: "github_issues", label: "GitHub Issues" },
      { key: "gitlab_issues", label: "GitLab Issues" },
      { key: "linear", label: "Linear" },
      { key: "monday", label: "monday.com" },
      { key: "servicenow", label: "ServiceNow" },
      { key: "zendesk", label: "Zendesk" },
      { key: "freshdesk", label: "Freshdesk" },
      { key: "pagerduty", label: "PagerDuty" },
      { key: "salesforce", label: "Salesforce Service Cloud" },
    ],
  },
  {
    id: "logs",
    title: { pt: "Logs e observabilidade", en: "Logs & observability", es: "Logs y observabilidad" },
    desc: {
      pt: "Onde o sintoma aparece. Erros, traces e métricas no intervalo do incidente, cruzados com o card.",
      en: "Where the symptom shows up. Errors, traces and metrics in the incident window, correlated with the card.",
      es: "Donde aparece el síntoma. Errores, trazas y métricas en la ventana del incidente, cruzados con el ticket.",
    },
    items: [
      { key: "aws", label: "AWS CloudWatch" },
      { key: "datadog", label: "Datadog" },
      { key: "sentry", label: "Sentry" },
      { key: "newrelic", label: "New Relic" },
      { key: "splunk", label: "Splunk" },
      { key: "grafana_loki", label: "Grafana Loki" },
      { key: "azure_monitor", label: "Azure Monitor" },
      { key: "gcp_logging", label: "Google Cloud Logging" },
      { key: "opensearch", label: "OpenSearch" },
    ],
  },
  {
    id: "databases",
    title: { pt: "Bancos de dados", en: "Databases", es: "Bases de datos" },
    desc: {
      pt: "Onde está o estado real. Consultas somente leitura para confirmar ou descartar cada hipótese.",
      en: "Where the real state lives. Read-only queries to confirm or rule out each hypothesis.",
      es: "Donde vive el estado real. Consultas de solo lectura para confirmar o descartar cada hipótesis.",
    },
    items: [
      { key: "postgres", label: "PostgreSQL" },
      { key: "mysql", label: "MySQL" },
      { key: "mssql", label: "Microsoft SQL Server" },
      { key: "mongodb", label: "MongoDB" },
      { key: "redis", label: "Redis" },
      { key: "dynamodb", label: "Amazon DynamoDB" },
      { key: "elasticsearch", label: "Elasticsearch" },
      { key: "oracle", label: "Oracle Database", comingSoon: true },
    ],
  },
  {
    id: "repos",
    title: { pt: "Repositórios de código", en: "Code repositories", es: "Repositorios de código" },
    desc: {
      pt: "Onde está a causa. O Ratchet indexa o código para ligar o erro à linha, ao commit e ao PR que o introduziu.",
      en: "Where the cause lives. Ratchet indexes the code to tie the error to the line, commit and PR that introduced it.",
      es: "Donde está la causa. Ratchet indexa el código para ligar el error a la línea, el commit y el PR que lo introdujo.",
    },
    items: [
      { key: "github_repo", label: "GitHub" },
      { key: "gitlab_repo", label: "GitLab" },
      { key: "bitbucket_repo", label: "Bitbucket" },
      { key: "azure_devops_repo", label: "Azure Repos" },
    ],
  },
  {
    id: "ai",
    title: { pt: "Provedores de IA", en: "AI providers", es: "Proveedores de IA" },
    desc: {
      pt: "Use sua própria chave ou modelo local. Você escolhe o provedor, o Ratchet orquestra a investigação.",
      en: "Bring your own key or a local model. You pick the provider, Ratchet orchestrates the investigation.",
      es: "Usa tu propia clave o un modelo local. Tú eliges el proveedor, Ratchet orquesta la investigación.",
    },
    items: [
      { key: "anthropic_claude", label: "Anthropic Claude" },
      { key: "openai", label: "OpenAI" },
      { key: "gemini", label: "Google Gemini" },
      { key: "bedrock", label: "AWS Bedrock" },
      { key: "vertex", label: "Google Vertex AI" },
      { key: "openrouter", label: "OpenRouter" },
      { key: "mistral", label: "Mistral" },
      { key: "deepseek", label: "DeepSeek" },
      { key: "groq", label: "Groq" },
      { key: "github_models", label: "GitHub Models" },
      { key: "codex_oauth", label: "OpenAI Codex" },
      { key: "xiaomi_mimo", label: "Xiaomi MiMo" },
      { key: "ollama", label: "Ollama (local)" },
    ],
  },
  {
    id: "chat",
    title: { pt: "Chat e notificações", en: "Chat & notifications", es: "Chat y notificaciones" },
    desc: {
      pt: "Onde o time conversa. Avisos de investigação concluída e o resumo onde a discussão já acontece.",
      en: "Where the team talks. Investigation-done alerts and the summary where the discussion already happens.",
      es: "Donde conversa el equipo. Avisos de investigación terminada y el resumen donde ya ocurre la discusión.",
    },
    items: [
      { key: "slack", label: "Slack" },
      { key: "msteams", label: "Microsoft Teams" },
      { key: "discord", label: "Discord" },
    ],
  },
];

export const INTEGRATIONS_TOTAL = INTEGRATION_CATEGORIES.reduce((n, c) => n + c.items.length, 0);
